import { Avatar, List, message, Row } from 'antd'
import CantileverButton, {
  EditButton,
  TrashButton,
} from 'components/CantileverButton'
import { ConfirmDeletePopup } from 'components/ConfirmDeletePopup'
import { TextInput } from 'components/form/inputs/TextInput'
import dayjs from 'dayjs'
import { useRolodexEdit } from 'features/fde'
import React, { useState } from 'react'
import styled from 'styled-components'
import {
  GeckoUserComment,
  RolodexEntryPatchJson,
  RolodexEntryType,
} from 'types'

const StyledAvatar = styled(Avatar)`
  background-color: ${({ theme }) => theme.forecast.background4};
`

const ActionsContainer = styled.div`
  display: flex;
  gap: 0.25rem;
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 2;
`

const CommentItemContainer = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  position: relative;
  &:hover ${ActionsContainer} {
    opacity: 1;
    pointer-events: auto;
  }
`

const CommentText = styled.p`
  color: ${({ theme }) => theme.forecast.text1};
  margin-bottom: 0;
  margin-right: 2.5rem;
  word-break: break-word;
`

const UserName = styled.div`
  color: ${({ theme }) => theme.forecast.text3};
  font-size: 0.7rem;
  margin-right: 0.5rem;
`

const CommentDate = styled.div`
  color: ${({ theme }) => theme.forecast.text4};
  font-size: 0.7rem;
`

const EditControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`

export const CommentItem: React.FC<{
  comment: GeckoUserComment
  forceRefresh: () => void
}> = ({ comment, forceRefresh }) => {
  const { rolodexPatch, rolodexDelete } = useRolodexEdit()
  const [isEditing, setIsEditing] = useState(false)
  // TODO: Update comment item here to use RichTextEditorLexical
  const [editValue, setEditValue] = useState<string>(comment.data.text)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [messageApi, contextHolder] = message.useMessage()

  const userInitials = comment.data.user_name
    ? comment.data.user_name
        .split(' ')
        .map((n) => n[0])
        .join('')
    : ''

  const commentDate = dayjs.unix(comment.data.timestamp).format('MMM D, YYYY')

  const handleSaveEdit = async () => {
    if (editValue.trim() === comment.data.text.trim()) {
      setIsEditing(false)
      return
    }
    setIsLoading(true)
    const patch: RolodexEntryPatchJson = {
      id: comment.id,
      type: RolodexEntryType.USER_COMMENT,
      data: {
        text: editValue,
      },
    }
    try {
      await rolodexPatch([patch])
      setIsEditing(false)
      forceRefresh()
    } catch (e) {
      console.error(e)
      messageApi.error('Failed to save comment')
    }
    setIsLoading(false)
  }

  const handleDelete = async () => {
    setIsLoading(true)
    try {
      await rolodexDelete(comment.id)
      setIsDeleteModalOpen(false)
      forceRefresh()
    } catch (e) {
      console.error(e)
      messageApi.error('Failed to delete comment')
    }
    setIsLoading(false)
  }

  return (
    <>
      {contextHolder}
      <CommentItemContainer>
        <List.Item style={{ width: '100%' }}>
          <List.Item.Meta
            avatar={
              <StyledAvatar style={{ marginLeft: '0.5rem' }}>
                {userInitials}
              </StyledAvatar>
            }
            title={
              <Row>
                <UserName>{comment.data.user_name}</UserName>
                <CommentDate>{commentDate}</CommentDate>
              </Row>
            }
            description={
              isEditing ? (
                <EditControlsContainer>
                  <TextInput
                    type='textarea'
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    mode='edit'
                    style={{
                      minWidth: 200,
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      flex: 1,
                    }}
                    autoFocus
                  />
                  <CantileverButton
                    onClick={handleSaveEdit}
                    disabled={isLoading || editValue.trim() === ''}>
                    Save
                  </CantileverButton>
                  <CantileverButton
                    onClick={() => {
                      setIsEditing(false)
                      setEditValue(comment.data.text)
                    }}>
                    Cancel
                  </CantileverButton>
                </EditControlsContainer>
              ) : (
                <CommentText>{comment.data.text}</CommentText>
              )
            }
          />
          {!isEditing && (
            <ActionsContainer>
              <EditButton
                onClick={() => setIsEditing(true)}
                title='Edit comment'
                disabled={isEditing || isLoading}
              />
              <TrashButton
                onClick={() => setIsDeleteModalOpen(true)}
                title='Delete comment'
                {...(isEditing || isLoading ? { disabled: true } : {})}
              />
            </ActionsContainer>
          )}
        </List.Item>
        {isDeleteModalOpen && (
          <ConfirmDeletePopup
            entityName='comment'
            popupPosition={{
              top: window.innerHeight / 2,
              left: window.innerWidth / 2,
            }}
            cancelDelete={() => setIsDeleteModalOpen(false)}
            confirmDelete={handleDelete}
            deleteMessage={
              'Are you sure you want to delete this comment? This action cannot be undone.'
            }
          />
        )}
      </CommentItemContainer>
    </>
  )
}
